import { useQuery } from "@tanstack/react-query";
import {
  fetchLiveGuardLocations,
  fetchGuardLocationHistory,
} from "../api/guardLocationApi";

export const guardLocationKeys = {
  live: ["guard-locations", "live"] as const,
  mapFeed: (withIncidents: boolean, incidentsFrom: string) =>
    ["guard-locations", "live", "map-feed", withIncidents, incidentsFrom] as const,
  shiftLive: (shiftId: number) =>
    ["guard-locations", "shift", shiftId, "live"] as const,
  siteLive: (siteId: number) =>
    ["guard-locations", "site", siteId, "live"] as const,
  history: (shiftId: number, guardUserId: number) =>
    ["guard-locations", "shift", shiftId, "guard", guardUserId, "history"] as const,
  playback: (shiftId: number, guardUserId: number) =>
    ["guard-locations", "shift", shiftId, "guard", guardUserId, "playback"] as const,
};

export const useLiveGuardLocations = () =>
  useQuery({
    queryKey: guardLocationKeys.live,
    queryFn: () => fetchLiveGuardLocations(),
    staleTime: 0,
    refetchInterval: 10_000,
  });

export const useLiveMapFeed = (options: { withIncidents: boolean; incidentsFrom: string }) =>
  useQuery({
    queryKey: guardLocationKeys.mapFeed(options.withIncidents, options.incidentsFrom),
    queryFn: () =>
      fetchLiveGuardLocations(
        options.withIncidents
          ? { include: "incidents", incidentsFrom: options.incidentsFrom }
          : undefined,
      ),
    staleTime: 0,
    refetchInterval: 10_000,
    // The window rolls at midnight and permissions resolve after first paint; without this the
    // map blanks itself on a key change and reads as "nobody is on duty".
    placeholderData: previous => previous,
  });

export const useShiftLiveLocations = (shiftId: number) =>
  useQuery({
    queryKey: guardLocationKeys.shiftLive(shiftId),
    queryFn: () => fetchLiveGuardLocations({ shiftId }),
    staleTime: 0,
    refetchInterval: 10_000,
    enabled: shiftId > 0,
  });

export const useSiteLiveLocations = (siteId: number) =>
  useQuery({
    queryKey: guardLocationKeys.siteLive(siteId),
    queryFn: () => fetchLiveGuardLocations({ siteId }),
    staleTime: 0,
    refetchInterval: 10_000,
    enabled: siteId > 0,
  });

export const useGuardLocationHistory = (
  shiftId: number,
  guardUserId: number | null
) =>
  useQuery({
    queryKey: guardLocationKeys.history(shiftId, guardUserId ?? 0),
    queryFn: () => fetchGuardLocationHistory(shiftId, guardUserId!),
    staleTime: 30_000,
    enabled: shiftId > 0 && guardUserId != null,
  });

export const useShiftLocationPlayback = (
  shiftId: number,
  guardUserId: number | null
) =>
  useQuery({
    queryKey: guardLocationKeys.playback(shiftId, guardUserId ?? 0),
    queryFn: () => fetchGuardLocationHistory(shiftId, guardUserId!),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: shiftId > 0 && guardUserId != null,
  });
